import { isEmpty } from '@sgme/fp';
import classnames from 'classnames';
import type { IntlKey } from 'locales';
import { AvailableItems } from 'models/forms/fields/AvailableItemsField';
import { ChangeEvent, CSSProperties, FC, useCallback } from 'react';
import { useIntl } from 'react-intl';

interface RawCompactListProps {
  id: string;
  availableItems: AvailableItems;
  isInvalid?: boolean;
  value: Nullable<string>;
  onValueChanged: (value: string) => void;
  isReadOnly?: boolean;
  isDisabled?: boolean;
  style?: CSSProperties;
}
export const RawCompactList: FC<RawCompactListProps> = ({
  id,
  isInvalid,
  isReadOnly,
  isDisabled,
  value,
  onValueChanged,
  availableItems: availableOptions,
  style,
}) => {
  const { formatMessage } = useIntl();

  const onChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      const newValue = e.target.value;
      if (newValue !== value) {
        onValueChanged(newValue);
      }
    },
    [onValueChanged, value],
  );

  const isSelectedMissing = !availableOptions.some(x => x.value === value);

  return (
    <select
      id={id}
      className={classnames('form-select', { 'is-warning': isInvalid, readonly: isReadOnly })}
      style={style}
      value={value || ''}
      onChange={onChange}
      disabled={isDisabled}
      data-e2e={`${id}-select`}
    >
      {isSelectedMissing && (
        <option key={value || 'null'} value={value ?? undefined}>
          {isEmpty(value)
            ? ''
            : formatMessage({ id: `form.${id}.${value}` as IntlKey, defaultMessage: value ?? undefined })}
        </option>
      )}
      {availableOptions.map(({ value: itemValue, label: itemLabel }) => (
        <option key={itemValue || 'null'} value={itemValue}>
          {itemValue === ''
            ? ''
            : itemLabel === ''
              ? ''
              : formatMessage({ id: `form.${id}.${itemValue}` as IntlKey, defaultMessage: itemLabel })}
        </option>
      ))}
    </select>
  );
};

RawCompactList.displayName = 'RawCompactList';
